/* ==========================================================================
   Trade Counter Direct — theme layer script
   Progressive enhancement only: every feature here has a no-JS fallback
   (GET forms, :target drawer, native <details>, /cart page).
   No third-party code. window.tcd is set inline in layout/theme.liquid.
   ========================================================================== */
(function () {
  'use strict';
  const cfg = window.tcd || { vat: 1.2, moneyFormat: '£{{amount}}', routes: {} };
  const R = cfg.routes || {};
  const $ = (sel, root) => (root || document).querySelector(sel);
  const $$ = (sel, root) => Array.from((root || document).querySelectorAll(sel));
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isDesktop = () => window.matchMedia('(min-width: 990px)').matches;

  /* ---------- money ---------- */
  function money(cents) {
    const n = (Math.round(cents) / 100);
    const parts = n.toFixed(2).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const amount = parts.join('.');
    const fmt = (typeof cfg.moneyFormat === 'string' && cfg.moneyFormat) || '£{{amount}}';
    return fmt.replace(/\{\{\s*amount[^}]*\}\}/, amount).replace(/<[^>]+>/g, '');
  }
  function focusable(root) {
    return $$('a[href],button:not([disabled]),input:not([disabled]):not([type=hidden]),select,textarea,[tabindex]:not([tabindex="-1"])', root)
      .filter(el => el.offsetParent !== null || el === document.activeElement);
  }
  function trapFocus(container) {
    function onKey(e) {
      if (e.key !== 'Tab') return;
      const els = focusable(container);
      if (!els.length) return;
      const first = els[0], last = els[els.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    container.addEventListener('keydown', onKey);
    return () => container.removeEventListener('keydown', onKey);
  }

  /* ---------- cart API ---------- */
  const cart = {
    async fetchSections(ids) {
      const url = (R.root || '/') + '?sections=' + ids.join(',');
      const r = await fetch(url, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      return r.ok ? r.json() : {};
    },
    async add(items, opts) {
      const body = { items: Array.isArray(items) ? items : [items] };
      const r = await fetch(R.cartAdd || '/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
      const data = await r.json();
      if (!r.ok) throw new Error(data.description || data.message || 'Could not add to basket');
      await cart.refresh();
      document.dispatchEvent(new CustomEvent('tcd:cart:added', { detail: { items: body.items, response: data, open: !opts || opts.open !== false } }));
      return data;
    },
    async change(line, quantity) {
      const r = await fetch(R.cartChange || '/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ line, quantity }) });
      const data = await r.json();
      if (!r.ok) throw new Error(data.description || data.message || 'Could not update basket');
      await cart.refresh();
      return data;
    },
    async changeById(id, quantity, properties) {
      const payload = { id: String(id), quantity };
      if (properties) payload.properties = properties;
      const r = await fetch(R.cartChange || '/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(payload) });
      const data = await r.json();
      if (!r.ok) throw new Error(data.description || data.message || 'Could not update basket');
      await cart.refresh();
      return data;
    },
    async refresh() {
      const ids = ['tcd-cart-drawer'];
      const header = $('[data-tcd-header]');
      const headerId = header && header.dataset.tcdSectionId;
      if (headerId) ids.push(headerId);
      let sections = {};
      try { sections = await cart.fetchSections(ids); } catch (e) { /* offline: leave DOM */ }
      // header cart trigger
      const headerHtml = headerId && sections[headerId];
      if (headerHtml) {
        const doc = new DOMParser().parseFromString(headerHtml, 'text/html');
        const fresh = doc.querySelector('[data-tcd-cart-trigger]');
        const cur = $('[data-tcd-cart-trigger]');
        if (fresh && cur) cur.innerHTML = fresh.innerHTML, cur.setAttribute('aria-label', fresh.getAttribute('aria-label') || '');
      }
      const drawerHtml = sections['tcd-cart-drawer'];
      if (drawerHtml) {
        const doc = new DOMParser().parseFromString(drawerHtml, 'text/html');
        const fresh = doc.querySelector('[data-tcd-cart-drawer-body]');
        const cur = $('[data-tcd-cart-drawer-body]');
        if (fresh && cur) cur.innerHTML = fresh.innerHTML;
      }
      $$('[data-tcd-cart-region]').forEach(el => el.setAttribute('aria-busy', 'false'));
      $$('.tcd-qty[aria-busy]').forEach(el => el.removeAttribute('aria-busy'));
      $$('[data-tcd-cart-drawer] [aria-busy="true"]').forEach(el => el.setAttribute('aria-busy', 'false'));
      document.dispatchEvent(new CustomEvent('tcd:cart:refreshed'));
    }
  };

  /* ---------- add-to-cart forms (cards, PDP, upsells) ---------- */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-tcd-add]');
    if (!form) return;
    e.preventDefault();
    const btn = form.querySelector('[type=submit]');
    const idEl = form.querySelector('[name=id]');
    const qtyEl = form.querySelector('[name=quantity]');
    if (!idEl) return form.submit();
    const item = { id: Number(idEl.value), quantity: qtyEl ? Math.max(1, parseInt(qtyEl.value, 10) || 1) : 1 };
    const props = {};
    $$('[name^="properties["]', form).forEach(p => { if (p.type === 'checkbox' && !p.checked) return; const k = p.name.slice(11, -1); if (p.value !== '') props[k] = p.value; });
    if (Object.keys(props).length) item.properties = props;
    const extra = form.dataset.tcdAddExtra ? JSON.parse(form.dataset.tcdAddExtra) : [];
    const errEl = form.querySelector('[data-tcd-add-error]');
    if (errEl) errEl.hidden = true;
    if (btn) { btn.setAttribute('aria-busy', 'true'); btn.disabled = true; }
    try {
      await cart.add([item].concat(extra));
      if (btn) { btn.classList.add('is-added'); setTimeout(() => btn.classList.remove('is-added'), 2000); }
    } catch (err) {
      if (errEl) { errEl.textContent = err.message; errEl.hidden = false; } else { alert(err.message); }
    } finally {
      if (btn) { btn.removeAttribute('aria-busy'); btn.disabled = false; }
    }
  });

  /* ---------- quantity steppers ---------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-tcd-qty-step]');
    if (!btn) return;
    const wrap = btn.closest('.tcd-qty');
    const input = wrap && wrap.querySelector('input');
    if (!input) return;
    const step = Number(btn.dataset.tcdQtyStep) || 1;
    const min = Number(input.min) || 1;
    const max = input.max ? Number(input.max) : Infinity;
    const next = Math.min(max, Math.max(min, (parseInt(input.value, 10) || min) + step));
    if (next !== Number(input.value)) { input.value = next; input.dispatchEvent(new Event('change', { bubbles: true })); }
    wrap.querySelectorAll('[data-tcd-qty-step]').forEach(b => {
      const s = Number(b.dataset.tcdQtyStep);
      b.disabled = (s < 0 && next <= min) || (s > 0 && next >= max);
    });
  });

  /* ---------- header: mega menus ---------- */
  function initMega() {
    const nav = $('.tcd-nav');
    if (!nav) return;
    const items = $$('[data-tcd-mega]', nav);
    let openItem = null, hoverTimer = null, leaveTimer = null;
    function panelOf(item) { return item.querySelector('[data-tcd-mega-panel]'); }
    function trigger(item) { return item.querySelector('[data-tcd-mega-trigger]'); }
    function open(item) {
      if (openItem && openItem !== item) close(openItem);
      const p = panelOf(item); if (!p) return;
      p.hidden = false; trigger(item).setAttribute('aria-expanded', 'true'); openItem = item;
      document.addEventListener('keydown', onKey);
    }
    function close(item) {
      const it = item || openItem; if (!it) return;
      const p = panelOf(it); if (p) p.hidden = true;
      trigger(it).setAttribute('aria-expanded', 'false');
      if (openItem === it) openItem = null;
      document.removeEventListener('keydown', onKey);
    }
    function onKey(e) {
      if (e.key === 'Escape' && openItem) { const t = trigger(openItem); close(); t.focus(); }
    }
    items.forEach(item => {
      const t = trigger(item);
      t.addEventListener('click', (e) => { e.preventDefault(); openItem === item ? close(item) : open(item); });
      t.addEventListener('keydown', (e) => { if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(item); const first = focusable(panelOf(item))[0]; if (first) first.focus(); } });
      item.addEventListener('mouseenter', () => { clearTimeout(leaveTimer); hoverTimer = setTimeout(() => open(item), 200); });
      item.addEventListener('mouseleave', () => { clearTimeout(hoverTimer); leaveTimer = setTimeout(() => { if (openItem === item) close(item); }, 250); });
      item.addEventListener('focusout', (e) => { if (!item.contains(e.relatedTarget) && openItem === item) close(item); });
      $$('[data-tcd-mega-close]', item).forEach(b => b.addEventListener('click', () => { close(item); t.focus(); }));
    });
    document.addEventListener('click', (e) => { if (openItem && !openItem.contains(e.target)) close(); });

    // category panel: left pane switches right pane on hover/focus/click
    $$('[data-tcd-megacat]').forEach(mc => {
      const links = $$('[data-tcd-l1]', mc);
      const panes = $$('[data-tcd-pane]', mc);
      function show(i) {
        links.forEach(l => { const on = l.dataset.tcdL1 === String(i); l.classList.toggle('is-active', on); on ? l.setAttribute('aria-current', 'true') : l.removeAttribute('aria-current'); });
        panes.forEach(p => { p.hidden = p.dataset.tcdPane !== String(i); });
      }
      links.forEach(l => {
        let t;
        l.addEventListener('mouseenter', () => { t = setTimeout(() => show(l.dataset.tcdL1), 80); });
        l.addEventListener('mouseleave', () => clearTimeout(t));
        l.addEventListener('focus', () => show(l.dataset.tcdL1));
        l.addEventListener('keydown', (e) => {
          const i = links.indexOf(l);
          if (e.key === 'ArrowDown') { e.preventDefault(); (links[i + 1] || links[0]).focus(); }
          if (e.key === 'ArrowUp') { e.preventDefault(); (links[i - 1] || links[links.length - 1]).focus(); }
          if (e.key === 'ArrowRight') { e.preventDefault(); const pane = panes.find(p => !p.hidden); const f = pane && focusable(pane)[0]; if (f) f.focus(); }
        });
      });
    });
  }

  /* ---------- header: sticky flag ---------- */
  function initSticky() {
    const header = $('[data-tcd-header]');
    if (!header) return;
    const wrap = header.closest('.tcd-section-header') || header.parentElement;
    if (header.classList.contains('tcd-header--sticky') && wrap) wrap.classList.add('is-sticky');
  }

  /* ---------- mobile drawer ---------- */
  function initDrawer() {
    const drawer = $('[data-tcd-drawer]');
    if (!drawer) return;
    const panel = $('[data-tcd-drawer-panel]', drawer);
    let untrap = null, opener = null;
    function isOpen() { return drawer.classList.contains('is-open') || location.hash === '#menu'; }
    function open(by) {
      opener = by || document.activeElement;
      drawer.classList.add('is-open');
      document.body.classList.add('tcd-drawer-open');
      $$('[data-tcd-drawer-open]').forEach(b => b.setAttribute('aria-expanded', 'true'));
      untrap = trapFocus(panel);
      setTimeout(() => panel.focus(), 30);
    }
    function close() {
      drawer.classList.remove('is-open');
      document.body.classList.remove('tcd-drawer-open');
      $$('[data-tcd-drawer-open]').forEach(b => b.setAttribute('aria-expanded', 'false'));
      if (untrap) untrap(), untrap = null;
      if (location.hash === '#menu') history.replaceState(null, '', location.pathname + location.search);
      if (opener && opener.focus) opener.focus();
    }
    $$('[data-tcd-drawer-open]').forEach(b => b.addEventListener('click', (e) => { e.preventDefault(); open(b); cart.refresh(); }));
    $$('[data-tcd-drawer-close]', drawer).forEach(b => b.addEventListener('click', (e) => { e.preventDefault(); close(); }));
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && isOpen()) close(); });
    if (location.hash === '#menu') open();
    // tabs
    $$('[data-tcd-tab]', drawer).forEach(tab => tab.addEventListener('click', () => {
      $$('[data-tcd-tab]', drawer).forEach(t => { const on = t === tab; t.classList.toggle('is-active', on); t.setAttribute('aria-selected', on ? 'true' : 'false'); });
      $$('[data-tcd-tabpanel]', drawer).forEach(p => { p.hidden = p.dataset.tcdTabpanel !== tab.dataset.tcdTab; p.classList.toggle('is-active', !p.hidden); });
    }));
    // drill-down
    drawer.addEventListener('click', (e) => {
      const row = e.target.closest('[data-tcd-drill]');
      if (row) {
        e.preventDefault();
        const li = row.closest('.tcd-drawer__li');
        li.classList.add('is-open'); row.setAttribute('aria-expanded', 'true');
        const sub = li.querySelector(':scope > [data-tcd-sub]');
        const levels = row.closest('[data-tcd-levels]');
        if (levels) levels.dataset.level = String(Number(levels.dataset.level || 1) + 1);
        const f = sub && (sub.querySelector('[data-tcd-back]') || focusable(sub)[0]);
        if (f) setTimeout(() => f.focus(), reduced ? 0 : 160);
        const pane = row.closest('.tcd-drawer__tabpanel'); if (pane) pane.scrollTop = 0;
        return;
      }
      const back = e.target.closest('[data-tcd-back]');
      if (back) {
        const li = back.closest('.tcd-drawer__li.is-open');
        if (li) {
          li.classList.remove('is-open');
          const row = li.querySelector(':scope > [data-tcd-drill]');
          if (row) { row.setAttribute('aria-expanded', 'false'); row.focus(); }
          const levels = back.closest('[data-tcd-levels]');
          if (levels) levels.dataset.level = String(Math.max(1, Number(levels.dataset.level || 2) - 1));
        }
      }
    });
  }

  /* ---------- predictive search ---------- */
  function initSearch() {
    const form = $('[data-tcd-search]');
    if (!form) return;
    const input = $('[data-tcd-search-input]', form);
    const results = $('[data-tcd-search-results]', form);
    const bar = $('.tcd-searchbar', form);
    const clearBtn = $('[data-tcd-search-clear]', form);
    const closeBtn = $('[data-tcd-search-close]', form);
    let timer = null, ctrl = null, lastQ = '', hi = -1;
    const popular = (results.dataset.popular || '').split('|').map(s => s.trim()).filter(Boolean);
    const contactUrl = results.dataset.contactUrl || '/pages/contact';

    function esc(s) { return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])); }
    function openOverlay() { if (!isDesktop()) { form.classList.add('is-open'); document.body.classList.add('tcd-search-open'); } }
    function closeAll() { results.hidden = true; input.setAttribute('aria-expanded', 'false'); form.classList.remove('is-open'); document.body.classList.remove('tcd-search-open'); hi = -1; }
    function show(html) { results.innerHTML = html; results.hidden = false; input.setAttribute('aria-expanded', 'true'); hi = -1; }
    function skeleton() {
      let rows = '';
      for (let i = 0; i < 4; i++) rows += '<div class="tcd-search__skel-row"><div class="tcd-skel"></div><div><div class="tcd-skel"></div><div class="tcd-skel" style="width:45%"></div></div></div>';
      show('<div class="tcd-search__skel"><span class="tcd-eyebrow tcd-search__heading">Products</span>' + rows + '</div>');
    }
    function priceHtml(p) {
      // the suggest API returns price_min/max and compare_at_price_min/max as strings
      const ex = Math.round(parseFloat(p.price_min != null ? p.price_min : p.price) * 100);
      if (!isFinite(ex)) return '';
      const exMax = Math.round(parseFloat(p.price_max != null ? p.price_max : p.price) * 100);
      const cmp = Math.round(parseFloat(p.compare_at_price_min != null ? p.compare_at_price_min : p.compare_at_price) * 100);
      const inc = Math.round(ex * cfg.vat);
      const from = isFinite(exMax) && exMax > ex ? 'From ' : '';
      let out = '<span class="tcd-search__pprice">';
      if (isFinite(cmp) && cmp > ex + 1) out += '<s class="tcd-search__pwas">RRP ' + money(Math.round(cmp * cfg.vat)) + '</s>';
      out += '<span class="tcd-search__pinc">' + from + money(inc) + '<small>inc VAT</small></span>';
      out += '<span class="tcd-search__pex">' + from + money(ex) + ' <small>ex VAT</small></span>';
      return out + '</span>';
    }

    function render(q, data) {
      const r = (data && data.resources && data.resources.results) || {};
      const products = r.products || [], collections = r.collections || [], articles = (r.articles || []).concat(r.pages || []), queries = r.queries || [];
      if (!products.length && !collections.length && !articles.length && !queries.length) {
        const chips = popular.map(p => '<a class="tcd-chip" href="' + esc(R.search || '/search') + '?q=' + encodeURIComponent(p) + '">' + esc(p) + '</a>').join('');
        show('<div class="tcd-search__empty"><svg class="tcd-icon" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M10.5 4a6.5 6.5 0 1 1 0 13 6.5 6.5 0 0 1 0-13zM15.3 15.3L20 20M8 8l5 5M13 8l-5 5"/></svg><h3>No results for \u201c' + esc(q) + '\u201d</h3><p>Try a part number, brand or a shorter word.</p>' + (chips ? '<span class="tcd-eyebrow tcd-search__heading" style="text-align:center">Popular</span><div class="tcd-search__popular">' + chips + '</div>' : '') + '<a class="tcd-link tcd-link--arrow" href="' + esc(contactUrl) + '">Ask our product team</a></div>');
        return;
      }
      const allUrl = esc(R.search || '/search') + '?q=' + encodeURIComponent(q) + '&options%5Bprefix%5D=last';
      let html = '<div class="tcd-search__cols">';
      // left: products
      html += '<div class="tcd-search__main">';
      if (products.length) {
        html += '<h3 class="tcd-search__tab">Products</h3><ul class="tcd-search__list" role="group">';
        products.slice(0, 5).forEach(p => {
          const img = p.featured_image && p.featured_image.url ? '<img src="' + esc(p.featured_image.url.replace(/(\.[a-z]+)(\?|$)/i, '_120x120$1$2')) + '" width="56" height="56" alt="" loading="lazy">' : '';
          html += '<li><a class="tcd-search__prod" role="option" href="' + esc(p.url) + '"><span class="tcd-search__thumb">' + img + '</span><span class="tcd-search__pinfo"><span class="tcd-search__ptitle">' + esc(p.title) + '</span>' + priceHtml(p) + '<span class="tcd-search__pvendor">' + esc(p.vendor || '') + '</span></span></a></li>';
        });
        html += '</ul>';
      }
      html += '<a class="tcd-search__all" role="option" href="' + allUrl + '">View all search results <span aria-hidden="true">\u2192</span></a></div>';
      // right rail: suggestions, then categories / advice when present
      const hasRail = queries.length || collections.length || articles.length;
      if (hasRail) {
        html += '<div class="tcd-search__rail">';
        if (queries.length) {
          html += '<h3 class="tcd-search__railhead">Suggestions</h3><ul class="tcd-search__list">';
          queries.slice(0, 6).forEach(s2 => { html += '<li><a class="tcd-search__sugg" role="option" href="' + esc(s2.url) + '">' + (s2.styled_text || esc(s2.text)) + '</a></li>'; });
          html += '</ul>';
        }
        if (collections.length) {
          html += '<h3 class="tcd-search__railhead">Categories</h3><ul class="tcd-search__list">';
          collections.slice(0, 4).forEach(c => { html += '<li><a class="tcd-search__sugg" role="option" href="' + esc(c.url) + '">' + esc(c.title) + '</a></li>'; });
          html += '</ul>';
        }
        if (articles.length) {
          html += '<h3 class="tcd-search__railhead">Advice</h3><ul class="tcd-search__list">';
          articles.slice(0, 3).forEach(a => { html += '<li><a class="tcd-search__sugg" role="option" href="' + esc(a.url) + '">' + esc(a.title) + '</a></li>'; });
          html += '</ul>';
        }
        html += '</div>';
      }
      html += '</div>';
      show(html);
    }
    async function query(q) {
      if (ctrl) ctrl.abort();
      ctrl = new AbortController();
      skeleton();
      try {
        const url = (R.suggest || '/search/suggest') + '.json?q=' + encodeURIComponent(q) + '&resources[type]=query,product,collection,article,page&resources[limit]=6&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,variants.title,vendor,variants.sku,variants.barcode,tag';
        const r = await fetch(url, { signal: ctrl.signal, headers: { Accept: 'application/json' } });
        const data = await r.json();
        if (q === lastQ) render(q, data);
      } catch (e) { if (e.name !== 'AbortError') { console.error('tcd search failed', e); closeAll(); } }
    }
    input.addEventListener('input', () => {
      const q = input.value.trim();
      bar.classList.toggle('has-value', q.length > 0);
      clearTimeout(timer);
      lastQ = q;
      if (q.length < 2) { results.hidden = true; input.setAttribute('aria-expanded', 'false'); return; }
      timer = setTimeout(() => query(q), 220);
    });
    input.addEventListener('focus', () => { openOverlay(); if (input.value.trim().length >= 2 && results.innerHTML) { results.hidden = false; input.setAttribute('aria-expanded', 'true'); } });
    input.addEventListener('keydown', (e) => {
      const opts = $$('[role=option]', results);
      if (e.key === 'ArrowDown' && opts.length) { e.preventDefault(); hi = Math.min(opts.length - 1, hi + 1); opts.forEach((o, i) => o.classList.toggle('is-hi', i === hi)); opts[hi].scrollIntoView({ block: 'nearest' }); input.setAttribute('aria-activedescendant', opts[hi].id || ''); }
      else if (e.key === 'ArrowUp' && opts.length) { e.preventDefault(); hi = Math.max(0, hi - 1); opts.forEach((o, i) => o.classList.toggle('is-hi', i === hi)); }
      else if (e.key === 'Enter' && hi >= 0 && opts[hi]) { e.preventDefault(); location.href = opts[hi].href; }
      else if (e.key === 'Escape') { if (!results.hidden) { results.hidden = true; input.setAttribute('aria-expanded', 'false'); } else { input.value = ''; bar.classList.remove('has-value'); closeAll(); input.blur(); } }
    });
    if (clearBtn) clearBtn.addEventListener('click', () => { input.value = ''; bar.classList.remove('has-value'); results.hidden = true; input.focus(); });
    if (closeBtn) closeBtn.addEventListener('click', () => { closeAll(); });
    document.addEventListener('click', (e) => { if (!form.contains(e.target) && !results.hidden) { results.hidden = true; input.setAttribute('aria-expanded', 'false'); } });
    form.addEventListener('submit', () => { if (input.value.trim() === '') { input.focus(); return false; } });
  }

  /* ---------- cart drawer ---------- */
  function initCartDrawer() {
    const drawer = $('[data-tcd-cart-drawer]');
    if (!drawer) return;
    const panel = $('[data-tcd-cart-panel]', drawer);
    let untrap = null, opener = null;
    function open(by) {
      opener = by || document.activeElement;
      drawer.classList.add('is-open'); drawer.removeAttribute('hidden'); drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('tcd-cart-open');
      untrap = trapFocus(panel);
      const h = $('h2', panel); setTimeout(() => (h || panel).focus(), 30);
      document.dispatchEvent(new CustomEvent('tcd:cart:open'));
    }
    function close() {
      drawer.classList.remove('is-open'); drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('tcd-cart-open');
      setTimeout(() => { if (!drawer.classList.contains('is-open')) drawer.setAttribute('hidden', ''); }, reduced ? 0 : 220);
      if (untrap) untrap(), untrap = null;
      if (opener && opener.focus) opener.focus();
    }
    $$('[data-tcd-cart-trigger]').forEach(b => b.addEventListener('click', (e) => { e.preventDefault(); open(b); cart.refresh(); }));
    drawer.addEventListener('click', (e) => { if (e.target.closest('[data-tcd-cart-close]') || e.target.matches('[data-tcd-cart-scrim]')) { e.preventDefault(); close(); } });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && drawer.classList.contains('is-open')) close(); });
    document.addEventListener('tcd:cart:added', (e) => { if (e.detail.open !== false) open(); });
    // line quantity changes + removes inside the drawer / cart page
    let debounce = null;
    document.addEventListener('change', (e) => {
      const input = e.target.closest('[data-tcd-line-qty]');
      if (!input) return;
      const line = Number(input.dataset.tcdLineQty);
      const wrap = input.closest('.tcd-qty'); if (wrap) wrap.setAttribute('aria-busy', 'true');
      const region = input.closest('[data-tcd-cart-region]'); if (region) region.setAttribute('aria-busy', 'true');
      clearTimeout(debounce);
      debounce = setTimeout(async () => {
        try { await cart.change(line, Math.max(0, parseInt(input.value, 10) || 0)); }
        catch (err) { alert(err.message); await cart.refresh(); }
      }, 400);
    });
    document.addEventListener('click', async (e) => {
      const rm = e.target.closest('[data-tcd-line-remove]');
      if (!rm) return;
      e.preventDefault();
      const region = rm.closest('[data-tcd-cart-region]'); if (region) region.setAttribute('aria-busy', 'true');
      try { await cart.change(Number(rm.dataset.tcdLineRemove), 0); } catch (err) { alert(err.message); await cart.refresh(); }
    });
    // remove everything (JS path; the no-JS fallback posts updates[key]=0)
    document.addEventListener('click', async (e) => {
      const b = e.target.closest('[data-tcd-cart-clear]');
      if (!b) return;
      e.preventDefault();
      if (!window.confirm('Remove all items from your cart?')) return;
      b.setAttribute('aria-busy', 'true');
      const region = b.closest('[data-tcd-cart-region]') || b.closest('[data-tcd-cart-page]');
      if (region) region.setAttribute('aria-busy', 'true');
      try { await fetch('/cart/clear.js', { method: 'POST', headers: { 'Content-Type': 'application/json' } }); await cart.refresh(); }
      catch (err) { location.href = R.cart || '/cart'; }
      finally { b.removeAttribute('aria-busy'); }
    });
    // upsell tile add
    document.addEventListener('click', async (e) => {
      const b = e.target.closest('[data-tcd-upsell-add]');
      if (!b) return;
      e.preventDefault();
      b.setAttribute('aria-busy', 'true');
      try { await cart.add({ id: Number(b.dataset.tcdUpsellAdd), quantity: 1 }, { open: false }); b.classList.add('is-added'); b.textContent = 'Added'; }
      catch (err) { alert(err.message); }
      finally { b.removeAttribute('aria-busy'); }
    });
    // cart page: keep server-rendered region in sync after JS updates
    document.addEventListener('tcd:cart:refreshed', async () => {
      const page = $('[data-tcd-cart-page]');
      if (!page) return;
      try {
        const sections = await cart.fetchSections([page.dataset.tcdCartPage]);
        const html = sections[page.dataset.tcdCartPage];
        if (html) { const doc = new DOMParser().parseFromString(html, 'text/html'); const fresh = doc.querySelector('[data-tcd-cart-page]'); if (fresh) page.innerHTML = fresh.innerHTML; }
      } catch (e) { /* noop */ }
    });
  }

  /* ---------- despatch countdown (next-day cut-off, Europe/London) ---------- */
  function initCountdown() {
    document.querySelectorAll('[data-tcd-countdown]').forEach(el => {
      const out = el.querySelector('[data-tcd-countdown-text]') || el;
      out.textContent = 'Delivery options shown at checkout. Despatch times vary by item; delivery transit time starts after despatch.';
      el.dataset.state = 'information';
    });
  }

  /* ---------- copy to clipboard ---------- */
  document.addEventListener('click', async (e) => {
    const b = e.target.closest('[data-tcd-copy]');
    if (!b) return;
    e.preventDefault();
    try { await navigator.clipboard.writeText(b.dataset.tcdCopy); const t = b.querySelector('[data-tcd-copy-label]'); const prev = t ? t.textContent : ''; if (t) t.textContent = 'Copied'; setTimeout(() => { if (t) t.textContent = prev; }, 1600); } catch (err) { /* noop */ }
  });

  /* ---------- recently viewed ---------- */
  function trackRecent() {
    const el = $('[data-tcd-product-handle]');
    if (!el) return;
    try {
      const h = el.dataset.tcdProductHandle;
      let list = JSON.parse(localStorage.getItem('tcd_recent') || '[]').filter(x => x !== h);
      list.unshift(h); localStorage.setItem('tcd_recent', JSON.stringify(list.slice(0, 12)));
    } catch (e) { /* noop */ }
  }


  /* ---------- cart extras: discount code, note, recently viewed ---------- */
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-tcd-discount-apply]');
    if (!b) return;
    e.preventDefault();
    const wrap = b.closest('.tcd-totals__more-body');
    const input = wrap && wrap.querySelector('[data-tcd-discount-input]');
    const code = input && input.value.trim();
    if (!code) { input && input.focus(); return; }
    location.href = '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(location.pathname === (R.cart || '/cart') ? (R.cart || '/cart') : location.pathname);
  });
  let noteTimer = null;
  document.addEventListener('input', (e) => {
    const t = e.target.closest('[data-tcd-cart-note]');
    if (!t) return;
    clearTimeout(noteTimer);
    noteTimer = setTimeout(() => { fetch(R.cartUpdate || '/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: t.value }) }).catch(() => {}); }, 600);
  });
  async function renderRecent() {
    const box = $('[data-tcd-recent]'); if (!box) return;
    let handles = [];
    try { handles = JSON.parse(localStorage.getItem('tcd_recent') || '[]'); } catch (e) { return; }
    const cur = $('[data-tcd-product-handle]'); if (cur) handles = handles.filter(h => h !== cur.dataset.tcdProductHandle);
    handles = handles.slice(0, 2); if (!handles.length) return;
    const list = $('[data-tcd-recent-list]', box); let html = '';
    for (const h of handles) {
      try {
        const p = await (await fetch('/products/' + h + '.js')).json();
        const v = p.variants[0]; const inc = Math.round(v.price * cfg.vat);
        html += '<li class="tcd-upsell tcd-upsell--column"><a class="tcd-upsell__media" href="' + p.url + '">' + (p.featured_image ? '<img src="' + p.featured_image.replace(/(\.[a-z]+)(\?|$)/i, '_200x200$1$2') + '" alt="" loading="lazy">' : '') + '</a><div class="tcd-upsell__info"><a class="tcd-upsell__title" href="' + p.url + '">' + p.title.replace(/</g, '&lt;') + '</a><div class="tcd-price tcd-price--compact"><div class="tcd-price__lead"><span class="tcd-price__inc">' + money(inc) + '</span><span class="tcd-price__label">inc VAT</span></div><div class="tcd-price__ex"><span>' + money(v.price) + '</span><span class="tcd-price__label">ex VAT</span></div></div></div>' + (p.variants.length === 1 && v.available ? '<div class="tcd-upsell__action"><button class="tcd-btn tcd-btn--alt tcd-btn--sm" type="button" data-tcd-upsell-add="' + v.id + '">Add</button></div>' : '<div class="tcd-upsell__action"><a class="tcd-btn tcd-btn--alt tcd-btn--sm" href="' + p.url + '">View</a></div>') + '</li>';
      } catch (e) { /* skip */ }
    }
    if (html) { list.innerHTML = html; box.hidden = false; }
  }
  document.addEventListener('tcd:cart:open', renderRecent);
  document.addEventListener('tcd:cart:refreshed', () => { if ($('[data-tcd-recent]')) renderRecent(); });


  /* ---------- content pages: table of contents + scroll-spy; contact prefill; focus summaries ---------- */
  function initPages() {
    const src = $('[data-tcd-toc-source]');
    if (src) {
      const h2s = $$('h2', src);
      const lists = $$('[data-tcd-toc-list]');
      if (h2s.length && lists.length) {
        const items = h2s.map((h, i) => { if (!h.id) h.id = 'section-' + (h.textContent.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || i); return '<li><a href="#' + h.id + '">' + h.textContent.trim() + '</a></li>'; }).join('');
        lists.forEach(l => l.innerHTML = items);
        if ('IntersectionObserver' in window) {
          const io = new IntersectionObserver((entries) => { entries.forEach(e => { if (e.isIntersecting) { $$('[data-tcd-toc-list] a').forEach(a => a.classList.toggle('is-active', a.getAttribute('href') === '#' + e.target.id)); } }); }, { rootMargin: '-20% 0px -70% 0px' });
          h2s.forEach(h => io.observe(h));
        }
      }
    }
    const f = $('[data-tcd-focus]'); if (f) f.focus();
    const contact = $('[data-tcd-contact]');
    if (contact) {
      const reason = $('[data-tcd-reason]', contact), orderWrap = $('[data-tcd-order-field]', contact);
      const q = new URLSearchParams(location.search);
      function syncOrder() { if (orderWrap && reason) orderWrap.hidden = !/order/i.test(reason.value); }
      if (reason) {
        const pick = (re) => { const o = Array.from(reason.options).find(o => re.test(o.value)); if (o) reason.value = o.value; };
        if (q.get('product')) { pick(/product/i); const body = $('#contact-body'); if (body && !body.value) body.value = 'Question about ' + q.get('product') + ': '; }
        if (q.get('order')) { pick(/order/i); const on = $('#contact-order_number'); if (on && !on.value) on.value = q.get('order'); }
        reason.addEventListener('change', syncOrder); syncOrder();
      }
    }
  }


  /* ---------- brands index: JS-only search + disabled empty letters ---------- */
  function initBrandsIndex() {
    const root = $('[data-tcd-brandsidx]'); if (!root) return;
    const box = $('[data-tcd-brands-search]', root), input = $('[data-tcd-brands-input]', root); if (box) box.hidden = false;
    $$('[data-letter-empty]', root).forEach(e => { const a = $('[data-tcd-letters] a[data-letter="' + e.dataset.letterEmpty + '"]', root); if (a) a.setAttribute('aria-disabled', 'true'); });
    if (input) input.addEventListener('input', () => {
      const q = input.value.trim().toLowerCase(); let any = false;
      $$('[data-letter-group]', root).forEach(g => { let vis = 0; $$('li', g).forEach(li => { const on = !q || li.dataset.name.includes(q); li.hidden = !on; if (on) vis++; }); g.hidden = vis === 0; if (vis) any = true; });
      const none = $('[data-tcd-brands-none]', root); if (none) none.hidden = any;
    });
  }


  /* ---------- customer accounts: password eye, country/province selects ---------- */
  function initAccounts() {
    $$('[data-tcd-pw-toggle]').forEach(b => b.addEventListener('click', () => { const i = b.parentElement.querySelector('input'); const show = i.type === 'password'; i.type = show ? 'text' : 'password'; b.setAttribute('aria-label', show ? 'Hide password' : 'Show password'); }));
    $$('[data-tcd-country]').forEach(c => {
      const wrap = c.closest('.tcd-form__row'); const p = wrap && wrap.querySelector('[data-tcd-province]');
      if (!p || !window.Shopify || !Shopify.CountryProvinceSelector) return;
      new Shopify.CountryProvinceSelector(c.id, p.id, { hideElement: p.closest('[data-tcd-province-wrap]').id });
    });
  }


  /* ---------- rotating search placeholders (matches the current TCD site) ---------- */
  function initPlaceholders() {
    const input = $('[data-tcd-search-input]');
    if (!input) return;
    const list = [input.dataset.ph1, input.dataset.ph2, input.dataset.ph3].filter(Boolean);
    if (list.length < 2 || reduced) { if (list[0]) input.placeholder = list[0]; return; }
    let i = 0, ch = 0, deleting = false, timer = null, stopped = false;
    function tick() {
      if (stopped) return;
      const word = list[i];
      input.placeholder = word.slice(0, ch);
      if (!deleting && ch < word.length) { ch++; timer = setTimeout(tick, 100); }
      else if (!deleting) { deleting = true; timer = setTimeout(tick, 2400); }
      else if (ch > 0) { ch--; timer = setTimeout(tick, 60); }
      else { deleting = false; i = (i + 1) % list.length; timer = setTimeout(tick, 500); }
    }
    function stop() { stopped = true; clearTimeout(timer); input.placeholder = list[0]; }
    input.addEventListener('focus', stop, { once: true });
    input.addEventListener('input', stop, { once: true });
    timer = setTimeout(tick, 1200);
  }


  /* ---------- homepage: gentle reveal on scroll ---------- */
  function initReveal() {
    if (reduced || !('IntersectionObserver' in window)) return;
    const targets = $$('.tcd-home, .tcd-homeprods, .tcd-clearance').filter(el => !el.classList.contains('is-hidden'));
    if (!targets.length) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    targets.forEach((el, i) => {
      if (i === 0) return; // the hero paints immediately
      el.classList.add('tcd-reveal');
      io.observe(el);
    });
  }


  /* ---------- horizontal rows: show an edge fade only when there is more to scroll ---------- */
  function initScrollRows() {
    const rows = $$('.tcd-chiprow--scroll, .tcd-subnav__tiles, .tcd-gallery__thumbs, .tcd-footer__logos, .tcd-fbt__strip');
    if (!rows.length) return;
    rows.forEach(row => {
      const host = row.parentElement && row.parentElement.classList.contains('tcd-scrollrow') ? row.parentElement : row;
      if (host === row) row.classList.add('tcd-scrollrow');
      function paint() {
        const more = row.scrollWidth - row.clientWidth;
        if (more < 8) { row.removeAttribute('data-scroll'); return; }
        const state = [];
        if (row.scrollLeft > 4) state.push('start');
        if (row.scrollLeft < more - 4) state.push('end');
        row.setAttribute('data-scroll', state.join(' '));
      }
      row.addEventListener('scroll', () => { window.requestAnimationFrame(paint); }, { passive: true });
      window.addEventListener('on:debounced-resize', paint);
      window.addEventListener('resize', paint);
      paint();
    });
  }


  /* ---------- announcement bar: rotate through the messages on mobile ---------- */
  function initAnnouncement() {
    const rot = $('[data-tcd-annc-rotate]');
    if (!rot) return;
    const items = Array.from(rot.children);
    if (items.length < 2) return;
    if (reduced) return;
    const wait = (Number(rot.dataset.tcdAnncRotate) || 5) * 1000;
    let i = 0, timer = null;
    function show(n) {
      items.forEach((li, k) => { li.hidden = k !== n; li.classList.toggle('is-active', k === n); });
    }
    function next() { i = (i + 1) % items.length; show(i); timer = setTimeout(next, wait); }
    timer = setTimeout(next, wait);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) { clearTimeout(timer); } else { clearTimeout(timer); timer = setTimeout(next, wait); }
    });
  }


  /* ---------- voice search (matches the mic on the current site) ---------- */
  function initSpeech() {
    const btn = $('[data-tcd-speech]');
    if (!btn) return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const input = $('[data-tcd-search-input]');
    if (!SR || !input) { btn.remove(); return; }
    btn.hidden = false;
    let rec = null, listening = false;
    btn.addEventListener('click', () => {
      if (listening && rec) { rec.stop(); return; }
      rec = new SR();
      rec.lang = document.documentElement.lang || 'en-GB';
      rec.interimResults = true;
      rec.continuous = false;
      rec.onstart = () => { listening = true; btn.setAttribute('aria-pressed', 'true'); btn.classList.add('is-listening'); input.focus(); };
      rec.onerror = () => { listening = false; btn.setAttribute('aria-pressed', 'false'); btn.classList.remove('is-listening'); };
      rec.onend = () => { listening = false; btn.setAttribute('aria-pressed', 'false'); btn.classList.remove('is-listening'); };
      rec.onresult = (e) => {
        const said = Array.from(e.results).map(r => r[0].transcript).join('').trim();
        if (!said) return;
        input.value = said;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        if (e.results[e.results.length - 1].isFinal) { rec.stop(); input.form.submit(); }
      };
      try { rec.start(); } catch (err) { /* already running */ }
    });
  }

  function init() {
    initSticky(); initMega(); initDrawer(); initSearch(); initSpeech(); initCartDrawer(); initCountdown(); initPlaceholders(); initReveal(); initScrollRows(); initAnnouncement(); trackRecent(); initPages(); initBrandsIndex(); initAccounts();
    document.documentElement.classList.add('tcd-js');
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  window.tcd = Object.assign(window.tcd || {}, { cart, money });
})();

(function tcdStockQuantities() {
  'use strict';
  function update() {
    document.querySelectorAll('[data-tcd-stock-quantity]').forEach(function (el) {
      var d = el.dataset;
      var held = Number(d.held);
      var requested = Number(d.inCart);
      if (d.cart !== 'true') {
        var root = el.closest('[data-tcd-product]');
        var input = root && root.querySelector('[data-tcd-product-form] [name="quantity"]');
        requested += Math.max(1, Number(input && input.value) || 1);
      }
      var extra = Math.max(0, requested - held);
      var text = held > 0
        ? (extra > 0 ? (d.orderable === 'true' ? d.splitText : d.shortText) : d.heldText)
        : (d.orderable === 'true' ? d.orderText : d.outText);
      el.textContent = text.replaceAll('[held]', String(held)).replaceAll('[extra]', String(extra));
    });
  }
  var controller;
  async function refreshProductStock() {
    var root = document.querySelector('[data-tcd-product]');
    if (!root) return;
    if (controller) controller.abort();
    controller = new AbortController();
    var variantId = root.dataset.variantId;
    var url = new URL(root.dataset.productUrl, location.origin);
    url.searchParams.set('variant', variantId);
    url.searchParams.set('section_id', root.dataset.sectionId);
    try {
      var response = await fetch(url, { cache: 'no-store', signal: controller.signal });
      if (!response.ok) return;
      var doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (root.dataset.variantId !== variantId) return;
      var current = root.querySelector('.tcd-buybox__stock');
      var fresh = doc.querySelector('.tcd-buybox__stock');
      if (current && fresh) current.innerHTML = fresh.innerHTML;
      update();
    } catch (error) {}
  }
  document.addEventListener('input', function (e) {
    if (e.target.matches('[name="quantity"]')) update();
  });
  document.addEventListener('change', function () { queueMicrotask(update); });
  document.addEventListener('tcd:variant:change', update);
  document.addEventListener('tcd:cart:refreshed', function () { update(); refreshProductStock(); });
  function refreshVisible() {
    refreshProductStock();
    if (document.querySelector('[data-tcd-cart-page], [data-tcd-cart-drawer].is-open') && window.tcd && window.tcd.cart) window.tcd.cart.refresh();
  }
  window.addEventListener('focus', refreshVisible);
  window.addEventListener('pageshow', function (e) { if (e.persisted) refreshVisible(); });
  update();
})();
